import { HardwareStatusHistory } from "@app/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { CpuTemperatureChart, CpuUtilizationChart, PowerUsageChart, RamUsageChart, StorageUsageChart } from "./charts";
import { AsyncMetricsChart } from "./charts/AsyncMetricsChart";
import { GCMetricsChart } from "./charts/GCMetricsChart";
import { Spinner } from "./loading";

type HardwareChartsPanelProps = {
    data: HardwareStatusHistory[] | undefined;
    mode?: "carousel" | "grid" | "miniRow";
    isLoading?: boolean;
    error?: string;
    chartHeight?: number;
    compact?: boolean;
    singleChart?: boolean;
};

type ChartDef = {
    key: string;
    title: string;
    render: (data: HardwareStatusHistory[], height?: number, overrides?: any) => React.ReactNode;
};

function buildChartDefs(): ChartDef[] {
    return [
        { key: "cpu", title: "CPU Utilization", render: (d, h, o) => <CpuUtilizationChart data={d.map(x => x.cpuUsage!)} title="CPU Utilization" height={h} chartjsOverrides={o} /> },
        { key: "ram", title: "RAM Usage", render: (d, h, o) => <RamUsageChart data={d.map(x => ({ ...x.ramUsage!, ...x.gcUsage! }))} title="RAM Usage" height={h} chartjsOverrides={o} /> },
        { key: "temp", title: "Temperature & Humidity", render: (d, h, o) => <CpuTemperatureChart data={d.map(x => x.cpuTemps!)} title="Temperature and Humidity" height={h} chartjsOverrides={o} /> },
        { key: "power", title: "Power Usage", render: (d, h, o) => <PowerUsageChart data={d.map(x => x.powerUsage!)} title="Power Usage" height={h} chartjsOverrides={o} /> },
        { key: "storage", title: "Storage", render: (d, h, o) => <StorageUsageChart data={d.map(x => x.storageUsage!)} title="Storage" height={h} chartjsOverrides={o} /> },
        { key: "async", title: "Async Metrics", render: (d, h, o) => <AsyncMetricsChart data={d.map(x => x.asyncUsage!)} title="Firmware Async Metrics" height={h} chartjsOverrides={o} /> },
        { key: "gc", title: "GC Metrics", render: (d, h, o) => <GCMetricsChart data={d.map(x => x.gcUsage!)} title="Firmware GC Metrics" height={h} chartjsOverrides={o} /> },
    ];
}

export const HardwareChartsPanel: React.FC<HardwareChartsPanelProps> = ({ data, mode = "carousel", isLoading = false, error, chartHeight, compact = false, singleChart = false }) => {
    const [primaryIndex, setPrimaryIndex] = useState<number>(0);

    const chartDefs = useMemo(() => buildChartDefs(), []);

    // Bump an epoch only when the data's content actually changes (length or first/last timestamp).
    // The epoch is used both as a key suffix (forces remount → fresh entry animation) and to gate
    // chart.js's per-update animation: refreshes that return the same content keep animation off so
    // chart.js doesn't transition when react-chartjs-2 hands it a freshly-built (but identical) data prop.
    // Note: ref mutation during render must be idempotent under StrictMode's double-invoke. We track
    // a "committed" epoch (updated post-commit via effect) to know if the current render is the one
    // introducing a new dataset — that's the render where we let chart.js animate.
    const sig = data?.length
        ? `${data.length}:${data[data.length - 1]?.statusTimestamp?.getTime() ?? ""}:${data[0]?.statusTimestamp?.getTime() ?? ""}`
        : undefined;
    const epochRef = useRef(0);
    const lastBumpedSigRef = useRef<string | undefined>(undefined);
    if (sig && sig !== lastBumpedSigRef.current) {
        epochRef.current += 1;
        lastBumpedSigRef.current = sig;
    }
    const epoch = epochRef.current;
    const committedEpochRef = useRef(0);
    const sigChanged = epoch !== committedEpochRef.current;
    useEffect(() => {
        committedEpochRef.current = epoch;
    }, [epoch]);
    const animOverride = sigChanged ? undefined : { options: { animation: false as const } };

    // miniRow state (kept for firmware_webconfig consumers)
    // The expanded popup is click-to-open, not hover-to-open: the mini row sits mid-page, so
    // opening on hover meant simply scrolling past it popped the panel over the content below
    // and left the reader to dismiss it every time. Hover still *steers* an already-open popup
    // (moving along the row swaps which chart it shows) and still closes it on the way out.
    const [expandedChart, setExpandedChart] = useState<number | null>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const compactOverrides = useMemo(
        () => {
            if (compact) {
                return {
                    options: {
                        ...(animOverride?.options ?? {}),
                        plugins: { title: { font: { size: 16, weight: "bolder" as const } } },
                        scales: { y: { ticks: { display: false } }, x: { ticks: { display: false } } },
                    },
                };
            }
            return animOverride;
        },
        [compact, animOverride]
    );

    // carousel data structures (kept for other consumers)
    const chartGroups = useMemo(() => {
        if (!data?.length) return [];
        return [
            [<CpuUtilizationChart key="cpu" data={data.map(x => x.cpuUsage!)} title="CPU Utilization" height={chartHeight} chartjsOverrides={compactOverrides as any} />, <RamUsageChart key="ram" data={data.map(x => ({ ...x.ramUsage!, ...x.gcUsage! }))} title="RAM Usage" height={chartHeight} chartjsOverrides={compactOverrides as any} />],
            [<CpuTemperatureChart key="temp" data={data.map(x => x.cpuTemps!)} title="Temperature and Humidity" height={chartHeight} chartjsOverrides={compactOverrides as any} />, <PowerUsageChart key="power" data={data.map(x => x.powerUsage!)} title="Power Usage" height={chartHeight} chartjsOverrides={compactOverrides as any} />],
            [<StorageUsageChart key="storage" data={data.map(x => x.storageUsage!)} title="Storage" height={chartHeight} chartjsOverrides={compactOverrides as any} />],
            [<AsyncMetricsChart key="async" data={data.map(x => x.asyncUsage!)} title="Firmware Async Metrics" height={chartHeight} chartjsOverrides={compactOverrides as any} />, <GCMetricsChart key="gc" data={data.map(x => x.gcUsage!)} title="Firmware GC Metrics" height={chartHeight} chartjsOverrides={compactOverrides as any} />]
        ];
    }, [data, chartHeight, compactOverrides]);

    const singleChartSlides = useMemo(() => {
        if (!data?.length) return [];
        return [
            <CpuUtilizationChart key="cpu" data={data.map(x => x.cpuUsage!)} title="CPU Utilization" height={chartHeight} />,
            <RamUsageChart key="ram" data={data.map(x => ({ ...x.ramUsage!, ...x.gcUsage! }))} title="RAM Usage" height={chartHeight} />,
            <CpuTemperatureChart key="temp" data={data.map(x => x.cpuTemps!)} title="Temperature and Humidity" height={chartHeight} />,
            <PowerUsageChart key="power" data={data.map(x => x.powerUsage!)} title="Power Usage" height={chartHeight} />,
            <StorageUsageChart key="storage" data={data.map(x => x.storageUsage!)} title="Storage" height={chartHeight} />,
            <AsyncMetricsChart key="async" data={data.map(x => x.asyncUsage!)} title="Firmware Async Metrics" height={chartHeight} />,
            <GCMetricsChart key="gc" data={data.map(x => x.gcUsage!)} title="Firmware GC Metrics" height={chartHeight} />,
        ];
    }, [data, chartHeight]);

    const [slideIndex, setSlideIndex] = useState(0);
    const totalSlides = singleChart ? singleChartSlides.length : chartGroups.length;

    if (error && !isLoading) {
        return <h2 className="text-center w-full py-8">{error}</h2>;
    }

    if (!data?.length && !isLoading) {
        return <h2 className="text-center w-full py-8 text-muted-body-text">No Data</h2>;
    }

    if (isLoading && !data?.length) {
        return <div className="flex items-center justify-center w-full py-12"><Spinner size={32} /></div>;
    }

    // ── miniRow mode (firmware_webconfig) ──
    if (mode === "miniRow" && data?.length) {
        const miniCompact = {
            options: {
                layout: { padding: 0 },
                ...(animOverride?.options ?? {}),
                plugins: { title: { display: false }, legend: { display: false } },
                scales: {
                    y: { ticks: { display: false }, grid: { display: false }, title: { display: false } },
                    y1: { display: false },
                    x: { ticks: { display: false }, grid: { display: false }, title: { display: false } },
                },
            },
        };
        const miniHeight = 80;
        const charts = chartDefs.map(def => ({ title: def.title.split(" ")[0], el: def.render(data, miniHeight, miniCompact) }));
        // Popup is mounted on click after the data has already arrived — never animate the expand.
        const expandedOverrides = { options: { animation: false as const } };
        const expandedCharts = chartDefs.map(def => def.render(data, 300, expandedOverrides));

        return (
            <div className="relative">
                <style>{`.mini-chart-wrapper > div { padding: 2px !important; } .mini-chart-wrapper canvas { margin: 0 !important; }`}</style>
                {/* Two-up on a phone: four 80px strips in a row were unreadable and scrolled sideways. */}
                <div className="grid grid-cols-2 gap-1 sm:flex sm:overflow-x-auto">
                    {charts.map((c, i) => (
                        <div
                            key={`${i}-${epoch}`}
                            className="flex-1 min-w-[80px] cursor-pointer hover:bg-gray-50 rounded transition-colors mini-chart-wrapper"
                            title="Click to expand"
                            onClick={() => { if (hoverTimeout.current) clearTimeout(hoverTimeout.current); setExpandedChart(i); }}
                            onMouseEnter={() => { if (hoverTimeout.current) clearTimeout(hoverTimeout.current); setExpandedChart(prev => (prev === null ? null : i)); }}
                            onMouseLeave={() => { hoverTimeout.current = setTimeout(() => setExpandedChart(null), 300); }}
                        >
                            <div className="text-[10px] font-semibold text-gray-500 text-center pt-1">{c.title}</div>
                            {c.el}
                        </div>
                    ))}
                </div>
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded pointer-events-none">
                        <Spinner size={24} />
                    </div>
                )}
                {expandedChart !== null && (
                    <div
                        ref={popupRef}
                        className="absolute left-0 right-0 top-full mt-1 z-40 bg-white border border-gray-200 rounded-lg shadow-lg p-4"
                        onMouseEnter={() => { if (hoverTimeout.current) clearTimeout(hoverTimeout.current); }}
                        onMouseLeave={() => { hoverTimeout.current = setTimeout(() => setExpandedChart(null), 300); }}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex gap-1">
                                {charts.map((c, i) => (
                                    <button
                                        key={i}
                                        onMouseEnter={() => setExpandedChart(i)}
                                        className={`px-2 py-1 text-xs rounded ${expandedChart === i ? "bg-brand-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                                    >
                                        {c.title}
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => setExpandedChart(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                        </div>
                        {expandedCharts[expandedChart]}
                    </div>
                )}
            </div>
        );
    }

    // ── grid mode: primary chart + thumbnail grid ──
    if (mode === "grid" && data?.length) {
        const thumbOverrides = {
            options: {
                ...(animOverride?.options ?? {}),
                plugins: {
                    title: { display: true, font: { size: 14, weight: "bold" as const } },
                    legend: { display: false },
                },
                scales: {
                    y: { ticks: { display: false }, grid: { display: false }, title: { display: false } },
                    y1: { display: false },
                    x: { ticks: { display: false }, grid: { display: false }, title: { display: false } },
                },
            },
        };

        const safePrimary = primaryIndex < chartDefs.length ? primaryIndex : 0;
        const secondaryDefs = chartDefs.filter((_, i) => i !== safePrimary);

        return (
            <div className="relative">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded">
                        <Spinner size={32} />
                    </div>
                )}
                <div key={`primary-${safePrimary}-${epoch}`} className="w-full mb-4">
                    {chartDefs[safePrimary].render(data, chartHeight ?? 330, animOverride)}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                    {secondaryDefs.map(def => {
                        const origIdx = chartDefs.indexOf(def);
                        return (
                            <div
                                key={`${def.key}-${epoch}`}
                                onClick={() => setPrimaryIndex(origIdx)}
                                className="cursor-pointer rounded-lg border border-transparent hover:border-brand-primary transition-colors overflow-hidden"
                            >
                                <div className="pointer-events-none">
                                    {def.render(data, 160, thumbOverrides)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // ── carousel mode (no auto-rotation) ──
    return (
        <div className="flex flex-col items-center space-y-4">
            {singleChart ? (
                <div className="w-full transition-all duration-500 ease-in-out">
                    {singleChartSlides[slideIndex]}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full transition-all duration-500 ease-in-out">
                    {chartGroups[slideIndex]?.map((chart, idx) => (
                        <div key={idx} className="w-full">
                            {chart}
                        </div>
                    ))}
                </div>
            )}
            {totalSlides > 1 && (
                <div className="flex items-center space-x-3">
                    <button className="p-1 text-muted-body-text hover:text-body-text" onClick={() => setSlideIndex(prev => (prev - 1 + totalSlides) % totalSlides)}>&larr;</button>
                    <button className="p-1 text-muted-body-text hover:text-body-text" onClick={() => setSlideIndex(prev => (prev + 1) % totalSlides)}>&rarr;</button>
                </div>
            )}
        </div>
    );
};
